import {
  useModalContext,
  useWSSnackbar,
  WSElement,
  WSFlexBox,
  WSFormOld,
  WSModal,
  WSSelectOld,
  WSText,
  WSTextInput
} from "@wingspanhq/fe-component-library";
import React from "react";
import { WSErrorMessage } from "../../../../components/WSErrorMessage/WSErrorMessage";
import { ErrorContextKey } from "../../../../services/platform";
import { useUserId } from "../../../../query/hooks/helpers";
import * as Yup from "yup";
import { CardSuccessPhysical } from "../BankCard/BankCard";
import { US_STATES_OPTIONS } from "../../../../constants/user";
import {
  useMemberProfile,
  useUserProfile
} from "../../../../query/users/queries";
import { WSQueries } from "../../../../query/WSQuery";
import {
  validatorAddressLine1,
  validatorAddressLine2,
  validatorFirstName,
  validatorLastName,
  validatorZipCode
} from "../../../../utils/validators";
import {
  useCreateCard,
  useReplaceCard
} from "../../../../query/payments/mutations";

export const CREATE_PHYSICAL_DEBIT_CARD_MODAL =
  "CREATE_PHYSICAL_DEBIT_CARD_MODAL";

export const CreatePhysicalCardModal: React.FC = () => {
  const { closeModal } = useModalContext();
  const { openSnackbar } = useWSSnackbar();

  const memberId = useUserId();
  const qUserProfile = useUserProfile(memberId);
  const qMemberProfile = useMemberProfile(memberId);
  const [createCard, createCardMeta] = useCreateCard();
  const [replaceCard, replaceCardMeta] = useReplaceCard();

  const onClose = () => closeModal(CREATE_PHYSICAL_DEBIT_CARD_MODAL);

  return (
    <WSModal name={CREATE_PHYSICAL_DEBIT_CARD_MODAL} size="S" onClose={onClose}>
      {({ isReplace, onSuccess, cardId }) => (
        <WSElement p="S">
          <WSText.Heading4 mb="XL">
            Where should we send your card?
          </WSText.Heading4>

          <WSFlexBox.Center>
            <CardSuccessPhysical />
          </WSFlexBox.Center>

          <WSQueries queries={{ qMemberProfile, qUserProfile }}>
            {({
              qMemberProfile: { data: member },
              qUserProfile: { data: user }
            }) => (
              <WSFormOld
                mt="2XL"
                defaultValues={{
                  firstName: user.profile.firstName || "",
                  lastName: user.profile.lastName || "",
                  addressLine1: member.profile.address?.addressLine1 || "",
                  addressLine2: member.profile.address?.addressLine2 || "",
                  city: member.profile.address?.city || "",
                  state: member.profile.address?.state || "",
                  zipCode: member.profile.address?.postalCode || ""
                }}
                validationSchema={Yup.object().shape({
                  firstName: validatorFirstName.required("Required"),
                  lastName: validatorLastName.required("Required"),
                  addressLine1: validatorAddressLine1,
                  addressLine2: validatorAddressLine2,
                  city: Yup.string().required("City is required"),
                  state: Yup.string().required("Required"),
                  zipCode: validatorZipCode
                })}
                onSubmit={async formData => {
                  const shippingAddress = {
                    country: "US",
                    city: formData.city,
                    postalCode: formData.zipCode,
                    state: formData.state,
                    addressLine1: formData.addressLine1,
                    addressLine2: formData.addressLine2
                  };

                  if (isReplace) {
                    await replaceCard({
                      id: cardId,
                      data: {
                        shippingAddress
                      }
                    });
                  } else {
                    await createCard(
                      {
                        requestPhysicalCard: true,
                        shippingAddress
                      },
                      {
                        onSuccess: () => {
                          openSnackbar({
                            duration: 5000,
                            type: "success",
                            message: "Card created successfully!"
                          });
                          onClose();
                          onSuccess();
                        },
                        onError: () => {
                          openSnackbar({
                            duration: 5000,
                            type: "warning",
                            message: "Failed to create a card!"
                          });
                        }
                      }
                    );
                  }
                }}
              >
                <WSFormOld.Field
                  mt="XL"
                  name="firstName"
                  component={WSTextInput}
                  label="First name"
                />
                <WSFormOld.Field
                  mt="XL"
                  name="lastName"
                  component={WSTextInput}
                  label="Last name"
                />
                <WSFormOld.Field
                  mt="XL"
                  name="addressLine1"
                  component={WSTextInput}
                  label="Address line 1"
                />
                <WSFormOld.Field
                  mt="XL"
                  name="addressLine2"
                  component={WSTextInput}
                  label="Address line 2 (optional)"
                />
                <WSFormOld.Field
                  mt="XL"
                  name="city"
                  component={WSTextInput}
                  label="City"
                />
                <WSFormOld.Field
                  mt="XL"
                  name="zipCode"
                  component={WSTextInput}
                  label="Zip code"
                />
                <WSFormOld.Field
                  mt="XL"
                  name="state"
                  component={WSSelectOld}
                  componentProps={{
                    options: US_STATES_OPTIONS,
                    placeholder: "Select"
                  }}
                  label="State"
                />

                <WSErrorMessage
                  mt="XL"
                  contextKey={ErrorContextKey.Wallet}
                  error={createCardMeta.error || replaceCardMeta.error}
                />

                <WSFormOld.SubmitButton
                  mt="XL"
                  data-testid="submitConfirmAddress"
                  fullWidth
                >
                  Confirm address
                </WSFormOld.SubmitButton>
              </WSFormOld>
            )}
          </WSQueries>
        </WSElement>
      )}
    </WSModal>
  );
};
